/**
 * *Open in Airlock* — linking this machine without typing a code (§5.25).
 *
 * The page asks the Worker for a code offered to *this* account, and hands it to
 * the desktop app as `airlock://link?code=…`. The app takes the grant and opens
 * the approval page back here, where the same account approves it. The code is
 * not a credential: an app that claims somebody else's offer is refused at the
 * approval, which is the only step that hands anything over — and the approval
 * takes the six digits the app shows once it claimed the code, so an offer
 * somebody else's app opened cannot be approved from here.
 *
 * Nothing on a web page can tell whether a link to another application opened
 * one, so this waits a moment and, if the page still has the person's
 * attention, says that Airlock may not be installed and where to get it.
 */
import { useEffect, useRef, useState } from 'react';
import type React from 'react';
import { color, font } from '../../theme/tokens';
import { Card, GhostButton, PrimaryButton } from '../../components/primitives';
import { api } from '../../lib/api';
import type { DeviceOffer, Me } from '../../lib/types';
import { failureText } from './shared';

/** Long enough for a browser's "Open Airlock?" prompt to have been answered. */
const STALL_MS = 4000;

export function OpenInAirlock({
  title = 'Open Airlock on this computer',
  onDownloads
}: {
  title?: string;
  onDownloads: () => void;
}): React.JSX.Element {
  const [offer, setOffer] = useState<DeviceOffer | null>(null);
  const [stalled, setStalled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
  }, []);

  const open = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setStalled(false);
    try {
      const next = await api.post<DeviceOffer>('/device/offer');
      setOffer(next);
      window.location.href = next.link;
      timer.current = window.setTimeout(() => {
        if (document.visibilityState === 'visible') setStalled(true);
      }, STALL_MS);
    } catch (failure) {
      setError(failureText(failure));
    } finally {
      setBusy(false);
    }
  };

  const code = offer ? `${offer.userCode.slice(0, 4)}-${offer.userCode.slice(4)}` : null;

  return (
    <Card padding={22} style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 16, fontWeight: 600 }}>{title}</div>
      <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: '6px 0 16px', lineHeight: 1.6 }}>
        Airlock links itself to this account and finds your organization’s projects waiting — nothing to type. Your
        browser asks to open the app, then the approval page opens here.
      </p>
      <PrimaryButton onClick={() => void open()}>{busy ? 'Opening' : offer ? 'Open it again' : 'Open in Airlock'}</PrimaryButton>
      {offer && (
        <p style={{ fontSize: 13, color: color.inkSubtle, margin: '14px 0 0', lineHeight: 1.6 }}>
          If your browser asked, allow it. Airlock asks whether to link itself, then shows six digits for{' '}
          <span style={{ fontFamily: font.mono, letterSpacing: '.06em' }}>{code}</span> and the approval page opens
          next — type the digits there to approve it. Nothing in Airlock? Update it: an older version does not take
          a code from a link.
        </p>
      )}
      {stalled && (
        <div style={{ marginTop: 14, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: color.warnText }}>Nothing opened? Airlock may not be installed on this computer yet.</span>
          <GhostButton size="sm" onClick={onDownloads}>
            Downloads
          </GhostButton>
        </div>
      )}
      {error && <p style={{ fontSize: 13.5, color: color.badText, margin: '14px 0 0' }}>{error}</p>}
    </Card>
  );
}

/**
 * Whether the button is worth offering: an account holding a seat whose
 * organization is paid up. Anybody else has no projects waiting in the app, and
 * a personal account links a machine from the app the way it always has.
 */
export function canOpenInAirlock(me: Me): boolean {
  return me.organizations.some(
    (organization) =>
      (organization.role === 'owner' || organization.role === 'admin' || organization.role === 'member') &&
      (organization.seatAccess === 'active' || organization.seatAccess === 'grace')
  );
}
